import {
  applyQueryConfig,
  createUiProps,
  responseStringTransformer,
  ServiceHandler,
} from '../utils';

import {
  serializePageableResponse,
  serializeElasticPageableTokenResponse,
} from './serializers/elastic.serializers';
import { FORMAT_DATE_AND_TIME, toUniversalUtcDate } from '../utils/date.utils';
import { standardQuerySerializer } from '.';
import { NOTES_QUERY_PARAM_MAPPING } from './notes/notes.param-mapping';

/* common */
export const getNote = ServiceHandler({
  url: '/api/quote/v1/note/:noteId',
  transformResponse: [
    responseStringTransformer,
    (data) => ({ data }),
    createUiProps(
      ['created', 'modified'],
      (date) =>
        date && toUniversalUtcDate(date, { format: FORMAT_DATE_AND_TIME })
    ),
  ],
});

export const deleteNote = ServiceHandler({
  url: '/api/quote/v1/note/:noteId',
  method: 'delete',
});

export const getAllNoteTypes = ServiceHandler({
  url: '/api/quote/v1/note/note-types',
});

/* accounts */
export const getAccountNotes = ServiceHandler({
  url: 'api/quote/v1/note/account/:accountId',
  transformResponse: [
    responseStringTransformer,
    serializePageableResponse,
    // `lastUpdated` is not a field on the note payload -- the service returns
    // `modified`, as `getClaimNote` below already reads. The listing therefore
    // produced `ui_lastUpdated: undefined`, and the row's tooltip rendered
    // "last updated undefined" on every note.
    createUiProps(['created', 'modified'], (date) => toUniversalUtcDate(date)),
  ],
});

/* quotes */
export const getQuoteNotes = ServiceHandler({
  url: '/api/quote/v1/note/quote/:quoteId',
  transformResponse: [
    responseStringTransformer,
    serializePageableResponse,
    // `lastUpdated` is not a field on the note payload -- the service returns
    // `modified`, as `getClaimNote` below already reads. The listing therefore
    // produced `ui_lastUpdated: undefined`, and the row's tooltip rendered
    // "last updated undefined" on every note.
    createUiProps(['created', 'modified'], (date) => toUniversalUtcDate(date)),
  ],
});

/* sales inbox */
export const getLeadNote = ServiceHandler({
  url: 'api/sales/v1/note/:noteId',
  transformResponse: [
    responseStringTransformer,
    (data) => ({ data }),
    createUiProps(
      ['created', 'lastUpdated'],
      (date) =>
        date && toUniversalUtcDate(date, { format: FORMAT_DATE_AND_TIME })
    ),
  ],
});

export const getLeadNotes = ServiceHandler({
  url: '/api/sales/v1/note/lead/:leadId',
  transformResponse: [
    responseStringTransformer,
    serializeElasticPageableTokenResponse,
    // `lastUpdated` is not a field on the note payload -- the service returns
    // `modified`, as `getClaimNote` below already reads. The listing therefore
    // produced `ui_lastUpdated: undefined`, and the row's tooltip rendered
    // "last updated undefined" on every note.
    createUiProps(['created', 'modified'], (date) => toUniversalUtcDate(date)),
  ],
});

export const deleteLeadNote = ServiceHandler({
  url: 'api/sales/v1/note/:noteId',
  method: 'delete',
});

/* claims */
export const createClaimsNote = ServiceHandler({
  url: '/api/claimsmgmt/v1/note',
  method: 'post',
});

export const getClaimNotes = ServiceHandler({
  url: '/api/claimsmgmt/v1/note/claim/:claimId',
  transformResponse: [
    responseStringTransformer,
    serializeElasticPageableTokenResponse,
    // `lastUpdated` is not a field on the note payload -- the service returns
    // `modified`, as `getClaimNote` below already reads. The listing therefore
    // produced `ui_lastUpdated: undefined`, and the row's tooltip rendered
    // "last updated undefined" on every note.
    createUiProps(['created', 'modified'], (date) => toUniversalUtcDate(date)),
  ],
});

export const getClaimNote = ServiceHandler({
  url: '/api/claimsmgmt/v1/note/:noteId',
  transformResponse: [
    responseStringTransformer,
    (data) => ({ data }),
    createUiProps(
      ['created', 'modified'],
      (date) =>
        date && toUniversalUtcDate(date, { format: FORMAT_DATE_AND_TIME })
    ),
  ],
});

export const updateClaimNote = ServiceHandler({
  url: 'api/claimsmgmt/v1/note/:noteId',
  method: 'put',
});

export const deleteClaimNote = ServiceHandler({
  url: 'api/claimsmgmt/v1/note/:noteId',
  method: 'delete',
});

export const getClaimNoteTypes = ServiceHandler({
  url: 'api/claimsmgmt/v1/note/note-types',
});

/* claims — vendor
 *
 * A separate surface from the endpoints above rather than the same ones with a vendor flag. The
 * server derives the note's scope from the caller's token, so a vendor can only ever read and
 * write notes scoped to their own account, and nothing in the request decides that. Vendors
 * cannot update or delete, which is why there is no counterpart to updateClaimNote here.
 */
export const createVendorClaimsNote = ServiceHandler({
  url: '/api/claimsmgmt/v1/vendor/note/claim/:claimId',
  method: 'post',
});

export const getVendorClaimNotes = ServiceHandler({
  url: '/api/claimsmgmt/v1/vendor/note/claim/:claimId',
  transformResponse: [
    responseStringTransformer,
    serializeElasticPageableTokenResponse,
    // `lastUpdated` is not a field on the note payload -- the service returns
    // `modified`, as `getClaimNote` below already reads. The listing therefore
    // produced `ui_lastUpdated: undefined`, and the row's tooltip rendered
    // "last updated undefined" on every note.
    createUiProps(['created', 'modified'], (date) => toUniversalUtcDate(date)),
  ],
});

export const getVendorClaimNote = ServiceHandler({
  url: '/api/claimsmgmt/v1/vendor/note/:noteId',
  transformResponse: [
    responseStringTransformer,
    (data) => ({ data }),
    createUiProps(
      ['created', 'modified'],
      (date) =>
        date && toUniversalUtcDate(date, { format: FORMAT_DATE_AND_TIME })
    ),
  ],
});

/* Claims Diary */
export const createDiaryEntry = ServiceHandler({
  url: '/api/claimsmgmt/v1/diary',
  method: 'post',
});

export const getDiaryEntry = ServiceHandler({
  url: '/api/claimsmgmt/v1/diary/:noteId',
  transformResponse: [
    responseStringTransformer,
    (data) => ({ data }),
    createUiProps(
      ['created', 'modified'],
      (date) =>
        date && toUniversalUtcDate(date, { format: FORMAT_DATE_AND_TIME })
    ),
  ],
});

export const updateDiaryEntry = ServiceHandler({
  url: '/api/claimsmgmt/v1/diary/:noteId',
  method: 'put',
});

export const deleteDiaryEntry = ServiceHandler({
  url: '/api/claimsmgmt/v1/diary/:id',
  method: 'delete',
});

export const getDiaryEntries = ServiceHandler({
  url: 'api/claimsmgmt/v1/diary',
  transformResponse: [
    responseStringTransformer,
    serializePageableResponse,
    // `lastUpdated` is not a field on the note payload -- the service returns
    // `modified`, as `getClaimNote` below already reads. The listing therefore
    // produced `ui_lastUpdated: undefined`, and the row's tooltip rendered
    // "last updated undefined" on every note.
    createUiProps(['created', 'modified'], (date) => toUniversalUtcDate(date)),
  ],
});

export const getDiaryEntriesByAccount = ServiceHandler({
  url: 'api/claimsmgmt/v1/diary/account/:accountId',
  transformResponse: [
    responseStringTransformer,
    serializePageableResponse,
    // `lastUpdated` is not a field on the note payload -- the service returns
    // `modified`, as `getClaimNote` below already reads. The listing therefore
    // produced `ui_lastUpdated: undefined`, and the row's tooltip rendered
    // "last updated undefined" on every note.
    createUiProps(['created', 'modified'], (date) => toUniversalUtcDate(date)),
  ],
});

export const getAllDiaryEntryTypes = ServiceHandler({
  url: '/api/claimsmgmt/v1/diary/note-types',
});

export const getNotes = ServiceHandler({
  url: '/api/quote/v1/note/account',
  paramsSerializer: (params) =>
    standardQuerySerializer(
      applyQueryConfig(NOTES_QUERY_PARAM_MAPPING, params)
    ),
});
